import React, { useEffect, useState, useRef } from 'react'

interface DialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title: React.ReactNode
  description?: React.ReactNode
  confirmText?: string
  cancelText?: string
  confirmVariant?: 'primary' | 'danger'
  isLoading?: boolean
  logo?: boolean
}

export const Dialog: React.FC<DialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmText = '확인',
  cancelText = '취소',
  confirmVariant = 'primary',
  isLoading = false,
  logo = false,
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    // 이전 타이머 정리
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }

    if (isOpen) {
      // 다이얼로그 표시
      setIsVisible(true)
      setIsAnimating(false) // 초기값 명시적으로 설정
      
      // DOM 렌더링을 기다린 후 애니메이션 시작
      timeoutRef.current = setTimeout(() => {
        setIsAnimating(true)
      }, 10) // 아주 짧은 지연으로 DOM 렌더링 보장
    } else if (isVisible) {
      // 다이얼로그 숨김 (이미 보이는 경우에만)
      setIsAnimating(false)
      
      // 애니메이션 완료 후 DOM에서 제거
      timeoutRef.current = setTimeout(() => {
        setIsVisible(false)
      }, 150) // 애니메이션 시간과 일치
    }

    // cleanup 함수
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [isOpen, isVisible])

  // 컴포넌트 언마운트시 타이머 정리
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  if (!isVisible) return null

  return (
    <>
      {/* Backdrop — 확인창이라 다른 모달(Modal z-6001 등) 위에 떠야 한다 */}
      <div 
        className={`fixed inset-0 bg-black z-[7000] transition-opacity duration-150 ${
          isAnimating ? 'bg-opacity-60' : 'bg-opacity-0'
        }`}
        onClick={onClose}
      />
      
      {/* Dialog */}
      <div 
        className="fixed inset-0 z-[7000] flex items-center justify-center p-4 pointer-events-none"
        onClick={onClose}
      >
        <div 
          className={`bg-white rounded-[20px] shadow-2xl max-w-[460px] w-full relative pointer-events-auto transition-all duration-150 ease-out ${
            isAnimating 
              ? 'opacity-100 translate-y-0 scale-100' 
              : 'opacity-0 translate-y-6 scale-95'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Container */}
          <div className="p-10">
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {/* Logo */}
            {logo && (
              <div className="flex justify-center mb-6">
                <img 
                  src="/assets/logo/datoz-logo.png" 
                  alt="POD24 Logo" 
                  className="h-12 object-contain"
                />
              </div>
            )}
            
            {/* Title */}
            <div className="text-center mb-8 mt-2">
              <h3 className="text-[22px] leading-[32px] text-gray-700">
                {title}
              </h3>
            </div>
            
            {/* Description */}
            {description && (
              <div className="text-center mb-10 text-[15px] text-gray-500 leading-relaxed">
                {description}
              </div>
            )}
            
            {/* Actions */}
            <div className="flex gap-[10px]">
              <button
                onClick={onClose}
                disabled={isLoading}
                className="flex-1 py-[15px] px-[20px] border border-[#e5e5e5] bg-white text-[#666] rounded-[8px] font-medium text-[16px] hover:bg-[#f5f5f5] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {cancelText}
              </button>
              <button
                onClick={onConfirm}
                disabled={isLoading}
                className={`flex-1 py-[15px] px-[20px] rounded-[8px] font-medium text-[16px] text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                  confirmVariant === 'danger' 
                    ? 'bg-[#dc3545] hover:bg-[#c82333] active:bg-[#bd2130]' 
                    : 'bg-[#2c5aa0] hover:bg-[#234a80] active:bg-[#1e3d6f]'
                }`}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    처리 중...
                  </span>
                ) : (
                  confirmText
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}